import { useRef, useState } from 'react'
import type { JSX, MouseEvent as ReactMouseEvent } from 'react'

import {
  Button,
  Flex,
  Heading,
  Link,
  Popover,
  Text,
} from '@buffer-mono/popcorn'
import { useUploader } from '@buffer-mono/uploader'

import { useSplitEnabled } from '@buffer-mono/features'

import { useAccount, useOrganizationId } from '~publish/contexts/AccountContext'

import { AttachmentPreview } from './components/AttachmentPreview'
import { DefaultTrigger } from './components/DefaultTrigger'
import { DropZone } from './components/DropZone'
import { FeedbackQuestion } from './components/FeedbackQuestion'
import { SuccessOverlay } from './components/SuccessOverlay'
import {
  ACCEPTED_FILE_TYPES,
  CONTEXT_QUESTION,
  FEEDBACK_UPLOADER_RESTRICTIONS,
  MAX_ATTACHMENTS,
  SUMMARY_QUESTION,
} from './FeedbackWidget.constants'
import type { FeedbackWidgetProps } from './FeedbackWidget.types'
import { useAttachments } from './hooks/useAttachments'
import { useFeedbackForm } from './hooks/useFeedbackForm'
import { openSupport } from './openSupport'

import styles from './FeedbackWidget.module.css'

/**
 * Keeps focus in the field while a dismiss control is pressed, so the field
 * never blurs and never validates on the way out. Suppressing the focus move is
 * what keeps the layout still long enough for the click to land.
 */
const dismissWithoutValidating = (event: ReactMouseEvent): void => {
  event.preventDefault()
}

const FeedbackWidget = ({
  id,
  source,
  children,
  initialSummary,
  closeButtonText,
  submitButtonText,
  align,
  className,
  onSubmit,
  onClose,
}: FeedbackWidgetProps): JSX.Element | null => {
  const organizationId = useOrganizationId()
  // Not gated on `isReady`: while Split loads, `isEnabled` is false and a
  // submission takes the Sheet path, which is a correct destination rather than
  // a fallback. Gating would block submits for the length of the SDK load.
  const { isEnabled: isFeatureOsRoutingEnabled } = useSplitEnabled(
    'feedback-to-featureos',
  )
  const { account } = useAccount()

  const [isOpen, setIsOpen] = useState(false)
  const [attachmentError, setAttachmentError] = useState<string | null>(null)
  const firstFieldRef = useRef<HTMLTextAreaElement>(null)
  const titleId = `${id}:title`

  const uploader = useUploader({
    id: `feedback-widget-${id}`,
    fileRestrictions: FEEDBACK_UPLOADER_RESTRICTIONS,
    userId: account?.id ?? '',
    organizationId: organizationId ?? '',
  })

  const {
    attachments,
    isDragging,
    hasUploadingAttachments,
    fileInputRef,
    handleFileSelect,
    handleDragEnter,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleRemoveAttachment,
    handleAttachClick,
    cleanupAttachments,
  } = useAttachments({
    uploader,
    setError: setAttachmentError,
  })

  const {
    summary,
    setSummary,
    context,
    setContext,
    summaryError,
    contextError,
    error: formError,
    validateSummaryField,
    validateContextField,
    isSubmitting,
    isSubmitted,
    handleSubmit,
    reset,
  } = useFeedbackForm({
    id,
    shouldFileToBoard: isFeatureOsRoutingEnabled,
    source: source ?? 'publish',
    organizationId,
    accountId: account?.id,
    email: account?.email,
    initialSummary,
    attachments,
    hasUploadingAttachments,
    cleanupAttachments,
    onSubmit,
    onAutoClose: () => setIsOpen(false),
  })

  // Errors from attachments are surfaced alongside the form-level ones; field
  // errors sit under their own field instead.
  const error = attachmentError ?? formError

  const handleOpen = (open: boolean): void => {
    setIsOpen(open)
    if (!open) {
      setAttachmentError(null)
      if (isSubmitted) {
        reset()
      }
    }
  }

  const handleClose = (): void => {
    handleOpen(false)
    reset()
    onClose?.()
  }

  const handleOpenAutoFocus = (event: Event): void => {
    if (!firstFieldRef.current) return

    event.preventDefault()
    firstFieldRef.current.focus()
  }

  return (
    <Popover open={isOpen} onOpenChange={handleOpen} modal>
      <Popover.Trigger>
        {children ?? (
          <DefaultTrigger
            id={id}
            isOpen={isOpen}
            hasData={!!summary || !!context || attachments.length > 0}
            className={className}
          />
        )}
      </Popover.Trigger>
      <Popover.Content
        className={styles.content}
        align={align}
        aria-modal="true"
        aria-labelledby={isSubmitted ? undefined : titleId}
        aria-label={isSubmitted ? 'Share Feedback' : undefined}
        onOpenAutoFocus={handleOpenAutoFocus}
      >
        {isSubmitted ? (
          <SuccessOverlay />
        ) : (
          <>
            <div className={styles.header}>
              <Heading as="h2" size="sm" id={titleId}>
                What would help Buffer work better for you?
              </Heading>
              {/* The route out for someone who opened the form wanting help
                  rather than wanting to suggest something. Before the fields,
                  so they do not answer two questions to find out this was the
                  wrong form. */}
              <Text size="sm" color="subtle">
                Something broken or need help?{' '}
                <Link
                  as="button"
                  type="button"
                  onMouseDown={dismissWithoutValidating}
                  onClick={(): void => {
                    handleOpen(false)
                    openSupport()
                  }}
                >
                  Contact support
                </Link>
                .
              </Text>
            </div>
            <form
              className={styles.form}
              aria-labelledby={titleId}
              onSubmit={handleSubmit}
            >
              <div className={styles.fields}>
                {/* Context is asked first: describing the task before the
                    problem is the order people naturally tell it in. The
                    answers still map the other way round — the "what might
                    help" answer becomes the suggestion title. */}
                <FeedbackQuestion
                  id={id}
                  name="context"
                  question={CONTEXT_QUESTION}
                  inputRef={firstFieldRef}
                  value={context}
                  error={contextError}
                  onChange={setContext}
                  onBlur={validateContextField}
                />

                <FeedbackQuestion
                  id={id}
                  name="summary"
                  question={SUMMARY_QUESTION}
                  value={summary}
                  error={summaryError}
                  onChange={setSummary}
                  onBlur={validateSummaryField}
                />

                <div className={styles.attachmentsSection}>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPTED_FILE_TYPES}
                    multiple
                    onChange={handleFileSelect}
                    className={styles.fileInput}
                    aria-label="Attach files"
                  />

                  {attachments.length > 0 && (
                    <div className={styles.attachmentPreviews}>
                      {attachments.map((attachment) => (
                        <AttachmentPreview
                          key={attachment.id}
                          attachment={attachment}
                          onRemove={handleRemoveAttachment}
                        />
                      ))}
                    </div>
                  )}

                  {attachments.length < MAX_ATTACHMENTS && (
                    <DropZone
                      isDragging={isDragging}
                      onDragEnter={handleDragEnter}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      onAttachClick={handleAttachClick}
                    />
                  )}

                  {attachments.length > 0 && (
                    <Text color="subtle" size="sm">
                      {attachments.length}/{MAX_ATTACHMENTS} files attached
                      {hasUploadingAttachments && ' (uploading...)'}
                    </Text>
                  )}
                </div>
              </div>

              {error && (
                <Text
                  color="critical"
                  size="sm"
                  role="alert"
                  id={`${id}:error`}
                >
                  {error}
                </Text>
              )}

              <Flex justify="end" gap="sm">
                <Button
                  type="button"
                  variant="tertiary"
                  // Leaving the form must not run the validation the person is
                  // leaving. Without this, pressing down here blurs the field,
                  // the message that appears pushes this button out from under
                  // the pointer, and the click never lands.
                  onMouseDown={dismissWithoutValidating}
                  onClick={(): void => handleClose()}
                  disabled={isSubmitting}
                >
                  {closeButtonText ?? 'Cancel'}
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting
                    ? 'Sending...'
                    : (submitButtonText ?? 'Send Feedback')}
                </Button>
              </Flex>
            </form>
          </>
        )}
      </Popover.Content>
    </Popover>
  )
}

export { FeedbackWidget }
export type { FeedbackWidgetProps }
